import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  InboxThread,
  InboxMail,
  InboxMailAttachment,
  InboxSummaryItem,
} from "../../pages/Crm/components/Inbox/utils/types";

interface CrmInboxState {
  threads: InboxThread[];
  pagination: {
    page: number;
    totalPages: number;
    hasNext: boolean;
    isLoadingMore: boolean;
  };
  isThreadsLoading: boolean;

  mails: InboxMail[];
  isMailsLoading: boolean;

  selectedThread: InboxThread | null;

  attachments: InboxMailAttachment[];

  summary: InboxSummaryItem[];
}

const initialState: CrmInboxState = {
  threads: [],
  pagination: {
    page: 1,
    totalPages: 1,
    hasNext: false,
    isLoadingMore: false,
  },
  isThreadsLoading: false,

  mails: [],
  isMailsLoading: false,

  selectedThread: null,

  attachments: [],

  summary: [],
};

const crmInboxSlice = createSlice({
  name: "crmInbox",
  initialState,
  reducers: {
    setThreads: (
      state,
      action: PayloadAction<{
        threads: InboxThread[];
        page: number;
        totalPages: number;
        hasNext: boolean;
      }>
    ) => {
      state.threads = action.payload.threads;
      state.pagination.page = action.payload.page;
      state.pagination.totalPages = action.payload.totalPages;
      state.pagination.hasNext = action.payload.hasNext;
    },
    appendThreads: (
      state,
      action: PayloadAction<{
        threads: InboxThread[];
        page: number;
        totalPages: number;
        hasNext: boolean;
      }>
    ) => {
      state.threads = [...state.threads, ...action.payload.threads];
      state.pagination.page = action.payload.page;
      state.pagination.totalPages = action.payload.totalPages;
      state.pagination.hasNext = action.payload.hasNext;
      state.pagination.isLoadingMore = false;
    },
    clearThreads: (state) => {
      state.threads = [];
      state.pagination = {
        page: 1,
        totalPages: 1,
        hasNext: false,
        isLoadingMore: false,
      };
    },
    updateThread: (state, action: PayloadAction<InboxThread>) => {
      const index = state.threads.findIndex(
        (t) => t.thread_id === action.payload.thread_id
      );
      if (index >= 0) {
        state.threads[index] = action.payload;
      }
      if (state.selectedThread?.thread_id === action.payload.thread_id) {
        state.selectedThread = action.payload;
      }
    },

    setMails: (state, action: PayloadAction<InboxMail[]>) => {
      state.mails = action.payload;
    },
    appendMail: (state, action: PayloadAction<InboxMail>) => {
      state.mails.push(action.payload);
    },
    clearMails: (state) => {
      state.mails = [];
    },

    setSelectedThread: (state, action: PayloadAction<InboxThread | null>) => {
      state.selectedThread = action.payload;
    },
    clearSelectedThread: (state) => {
      state.selectedThread = null;
    },

    setAttachments: (state, action: PayloadAction<InboxMailAttachment[]>) => {
      state.attachments = action.payload;
    },

    setSummary: (state, action: PayloadAction<InboxSummaryItem[]>) => {
      state.summary = action.payload;
    },

    setThreadsLoading: (state, action: PayloadAction<boolean>) => {
      state.isThreadsLoading = action.payload;
    },
    setMailsLoading: (state, action: PayloadAction<boolean>) => {
      state.isMailsLoading = action.payload;
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.pagination.isLoadingMore = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        totalPages: 1,
        hasNext: false,
        isLoadingMore: false,
      };
    },

    clearInboxState: () => initialState,
  },
});

export const {
  setThreads,
  appendThreads,
  clearThreads,
  updateThread,
  setMails,
  appendMail,
  clearMails,
  setSelectedThread,
  clearSelectedThread,
  setAttachments,
  setSummary,
  setThreadsLoading,
  setMailsLoading,
  setLoadingMore,
  resetPagination,
  clearInboxState,
} = crmInboxSlice.actions;

export default crmInboxSlice.reducer;
